// utils/exportExcel.js
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/**
 * columns: array of { name: "Header", selector: fn } OR array of header strings
 * rows: array of objects (final processedRows)
 * totals: object whose keys are header names e.g. { "Total Amount": 123, ... }
 */
export const exportTableToExcel = (columns = [], rows = [], totals = {}, fileName = "report") => {
  // Sanity checks
  if (!rows || !Array.isArray(rows) || rows.length === 0) {
    return alert("No rows to export");
  }

  // If columns is empty or not an array, try to infer headers:
  let headers = [];
  if (Array.isArray(columns) && columns.length > 0) {
    // columns might be objects like { name: "Date", selector: ... } or simple strings
    headers = columns.map(col => (typeof col === "string" ? col : (col && col.name) ? col.name : ""));
    // filter out any empty header names
    headers = headers.filter(h => h !== "");
  }

  // If still no headers, infer from first row keys (keep insertion order)
  if (!headers || headers.length === 0) {
    headers = Object.keys(rows[0] || {});
    if (headers.length === 0) {
      // fallback to totals keys
      headers = Object.keys(totals || {});
    }
  }

  // Build finalRows: ensure that each row object has keys matching headers in same order
  const finalRows = rows.map(r => {
    const out = {};
    headers.forEach(h => {
      // If value is undefined use empty string
      let val = r[h];
      // If value is numeric and you want to preserve numeric type, don't stringify
      // But if it is formatted string in UI, it may already be string; keep as is.
      out[h] = (typeof val === "undefined" || val === null) ? "" : val;
    });
    return out;
  });

  // Build footer row: fill totals into matching header columns
  const footerRow = {};
  headers.forEach(h => {
    // If totals has the exact header key, put it; otherwise blank
    if (totals && Object.prototype.hasOwnProperty.call(totals, h)) {
      footerRow[h] = totals[h];
    } else {
      footerRow[h] = ""; // keep empty if no total for this column
    }
  });

  // Append an empty row then footer row so Excel visually separates (optional)
  finalRows.push({}, footerRow);

  // Make worksheet and workbook
  const worksheet = XLSX.utils.json_to_sheet(finalRows, { skipHeader: false });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

  // Auto-width (optional): compute max width per column
  try {
    const colWidths = headers.map(h => {
      const maxLen = finalRows.reduce((max, row) => {
        const val = row[h] == null ? "" : String(row[h]);
        return Math.max(max, val.length);
      }, h.length);
      return { wch: Math.min(Math.max(maxLen + 2, 10), 50) }; // clamp
    });
    worksheet['!cols'] = colWidths;
  } catch (e) {
    // ignore if something breaks
  }

  // Write and save
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName}.xlsx`);
};
